import { useMemo, useState } from 'react';
import { Card } from '../../components';
import { ScrubChart, type ScrubPoint } from '../../components/charts/ScrubChart';
import { ScrubHeader } from '../../components/charts/ScrubHeader';
import type { AppData } from '../../data/types';
import { calculateDailyAllowance, txContributionInRange } from '../../lib/budget';
import { formatCurrency } from '../../lib/currency';
import { addDays, toIsoDate } from '../../lib/date';
import { getPayPeriod } from '../../lib/payPeriod';

type Props = {
  data: AppData;
  today: Date;
};

/**
 * Cumulative spending across the current pay period plotted against
 * the linear "budget pace" reference line. Color reflects how the
 * current cumulative compares to the pace target so far.
 */
export function SpendingTrend({ data, today }: Props) {
  const [scrub, setScrub] = useState<ScrubPoint | null>(null);

  const { rows, paceTarget, dayInPeriod, totalDays, todayPace, todaySpent } =
    useMemo(() => {
      const period = getPayPeriod(data.profile, today);
      const r = calculateDailyAllowance(data, today);
      const target = Math.max(0, r.discretionaryBudget);
      const dailyPace = period.totalDays > 0 ? target / period.totalDays : 0;
      // Plot one point per elapsed day, anchored at $0 the day before
      // the period starts so the line clearly climbs from zero — even
      // if all spending is concentrated on day 1. Future days are not
      // plotted; we don't want a flat tail through period end.
      const rows: ScrubPoint[] = [
        { time: toIsoDate(addDays(period.start, -1)), value: 0 },
      ];
      let cum = 0;
      let todayCum = 0;
      let todayPaceVal = 0;
      for (let i = 0; i < period.totalDays; i++) {
        const d = addDays(period.start, i);
        if (d.getTime() > today.getTime()) break;
        for (const t of data.transactions) {
          if (t.type !== 'expense') continue;
          cum += txContributionInRange(t, d, d);
        }
        todayCum = cum;
        todayPaceVal = dailyPace * (i + 1);
        rows.push({
          time: toIsoDate(d),
          value: Math.round(cum * 100) / 100,
        });
      }
      return {
        rows,
        paceTarget: target,
        dayInPeriod: r.period.dayInPeriod,
        totalDays: period.totalDays,
        todayPace: todayPaceVal,
        todaySpent: todayCum,
      };
    }, [data, today]);

  const currency = data.profile.currency;
  const fmt = (n: number) => formatCurrency(n, { currency });

  // Color rule: under pace by 10%+ green, within ±10% amber, over 10%+ red.
  const ratio = todayPace > 0 ? todaySpent / todayPace : 0;
  const lineColor =
    ratio < 0.9 ? '#0EA66B' : ratio > 1.1 ? '#E5484D' : '#E8B339';

  const paceDelta = todaySpent - todayPace;

  return (
    <Card elevation="sm" className="p-5 rounded-[24px]">
      <div className="flex items-start justify-between mb-3">
        <ScrubHeader
          title="Spending trend"
          subtitle="This period"
          currentValue={todaySpent}
          currentLabel={`Day ${dayInPeriod} of ${totalDays}`}
          scrubValue={scrub?.value ?? null}
          scrubLabel={scrub ? `Day ${rows.findIndex((r) => r.time === scrub.time) + 1} of ${totalDays}` : null}
          format={fmt}
          color={lineColor}
          delta={{
            value: -paceDelta,
            period: 'vs pace',
          }}
        />
        <span className="rounded-full px-3 py-1 bg-zinc-100 dark:bg-zinc-800 text-[12px] font-medium text-zinc-700 dark:text-zinc-300 shrink-0 tabular-nums">
          Pace: {fmt(paceTarget)}
        </span>
      </div>

      <ScrubChart
        data={rows}
        height={220}
        color={lineColor}
        areaFill
        priceLine={{ value: paceTarget, color: '#9CA3AF' }}
        onScrub={setScrub}
      />
    </Card>
  );
}
